/**
 * FlatCalc Calculation Utilities
 * Pure functions for roofing calculations
 */

import { GIRTH_BANDS, EDGE_DETAIL_TYPES } from '../constants/flatCalc';

/**
 * Format amount as GBP currency
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

/**
 * Generate unique ID for list items
 */
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Get girth band based on girth measurement
 */
export const getGirthBand = (girth) => {
  return GIRTH_BANDS.find(band => girth <= band.max) || GIRTH_BANDS[GIRTH_BANDS.length - 1];
};

/**
 * Calculate girth based on edge detail type, height and width
 */
export const calculateGirth = (type, height, width) => {
  const detailType = EDGE_DETAIL_TYPES.find(t => t.id === type);
  if (!detailType || !detailType.hasHeight) return 0;
  return detailType.hasWidth ? 150 + (height || 0) + (width || 0) : 150 + (height || 0);
};

/**
 * Get lap width for a product
 */
export const getLapWidth = (product) => {
  if (!product) return 100;
  if (product.lap_width_mm) return product.lap_width_mm;
  if (['PVC-FB', 'PVC', 'FPO'].includes(product.material)) return 100;
  if (product.sub_category === 'bituminous') return 75;
  return 100;
};

/**
 * Compute membrane runs for a roof area
 */
export const computeMembraneRuns = (rollSpec, roofLength, roofWidth, inverted) => {
  const rollDir = inverted ? roofLength : roofWidth;
  const spanDir = inverted ? roofWidth : roofLength;
  const effectiveCover = Math.max(rollSpec.rollWidth - rollSpec.lapWidth, 1);
  const numRuns = Math.ceil((spanDir * 1000) / effectiveCover);
  
  const overhangTotal = rollSpec.overhangSides === 'both' 
    ? rollSpec.overhang * 2 
    : rollSpec.overhangSides === 'single' 
      ? rollSpec.overhang 
      : 0;
  
  const runLengthM = (rollDir * 1000 + overhangTotal) / 1000;
  const totalLinM = numRuns * runLengthM;
  const totalRolls = rollSpec.rollLength > 0 ? Math.ceil(totalLinM / rollSpec.rollLength) : 0;
  
  return {
    numRuns,
    runLengthM,
    totalLinM,
    totalRolls,
    effectiveCover,
    numLaps: Math.max(numRuns - 1, 0),
    totalLapLengthM: Math.max(numRuns - 1, 0) * rollDir,
    perimeterM: 2 * (roofLength + roofWidth)
  };
};

/**
 * Check if VCL product is polythene (loose laid, no primer needed)
 */
export const isPolytheneVcl = (product) => {
  if (!product) return false;
  const name = (product.name || '').toLowerCase();
  return name.includes('polythene') || 
         name.includes('polyethylene') || 
         (product.sub_category || '').toLowerCase().includes('loose_laid');
};

/**
 * Calculate total area from areas array
 */
export const calculateTotalArea = (areas) => {
  return areas.reduce((sum, a) => {
    return sum + (a.length || 0) * (a.width || 0) - (a.deductions || 0);
  }, 0);
};
